import React, { useState, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';
import ModalVerEquipo from '../../ModalVerEquipo/ModalVerEquipo';
import Tutorial from '../../Tutorial/Tutorial';
import { TUTORIALES } from '../../../constants/tutorialesConfig';

const RANGOS = [
  { nombre: 'D',      min: 0,    max: 99,   icono: '⚪', color: '#64748b' },
  { nombre: 'Competidor',  min: 100,  max: 299,  icono: '🟢', color: '#22c55e' },
  { nombre: 'Veterano',    min: 300,  max: 599,  icono: '🔵', color: '#3b82f6' },
  { nombre: 'Elite',       min: 600,  max: 999,  icono: '🟣', color: '#a855f7' },
  { nombre: 'Maestro',     min: 1000, max: 1499, icono: '🟠', color: '#f97316' },
  { nombre: 'Leyenda',     min: 1500, max: 9998, icono: '🔴', color: '#ef4444' },
  { nombre: 'DTXI',        min: 9999, max: 99999,icono: '👑', color: '#f59e0b' },
];

const getRango = (pts) => RANGOS.find(r => pts >= r.min && pts <= r.max) || RANGOS[0];

const RankedPage = ({ userId }) => {
  const [tab, setTab]               = useState('perfil'); // perfil | buscar | historial | ranking
  const [perfil, setPerfil]         = useState(null);
  const [historial, setHistorial]   = useState([]);
  const [rankingGlobal, setRankingGlobal] = useState([]);
  const [visibleR, setVisibleR] = React.useState(10);
  const [equipoRival, setEquipoRival] = useState(null);
  const [desafiosPendientes, setDesafiosPendientes] = useState([]);
  const [busquedaNick, setBusquedaNick] = useState('');
  const [resultadoBusqueda, setResultadoBusqueda] = useState([]);
  const [buscando, setBuscando]     = useState(false);
  const [enviando, setEnviando]     = useState(false);
  const [msg, setMsg]               = useState('');
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    // ⚠️ NO BORRAR: cargar cache inmediatamente para evitar parpadeo
    try {
      const cached = JSON.parse(sessionStorage.getItem(`dtxi_ranked_${userId}`) || 'null');
      if (cached?.perfil) { setPerfil(cached.perfil); setLoading(false); }
    } catch(e) {}
    cargarTodo();
    const onFocus = () => cargarTodo();
    const onDesafioRespondido = () => cargarTodo();
    window.addEventListener('focus', onFocus);
    window.addEventListener('desafio_respondido', onDesafioRespondido);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('desafio_respondido', onDesafioRespondido);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarTodo = async () => {
    // ⚠️ NO BORRAR: no mostrar loading si ya hay datos (evita parpadeo)
    setLoading(prev => { if (!prev) return false; return true; });
    const [{ data: p }, { data: h }, { data: pend }] = await Promise.all([
      supabase.from('perfiles')
        .select('nickname, puntos_ranked, rango_ranked, victorias_ranked, derrotas_ranked, empates_ranked, racha_ranked, mejor_rango, avatar_color, pts_ciclo_actual, partidos_ciclo_actual, division_ranked')
        .eq('id', userId).single(),
      supabase.from('desafios')
        .select('*, retador:perfiles!desafios_retador_perfiles_fkey(nickname, avatar_color), retado:perfiles!desafios_retado_perfiles_fkey(nickname, avatar_color)')
        .or(`retador_id.eq.${userId},retado_id.eq.${userId}`)
        .in('tipo', ['ranked', 'por_los_puntos'])
        .order('created_at', { ascending: false })
        .limit(20),
      supabase.from('desafios')
        .select('*, retador:perfiles!desafios_retador_perfiles_fkey(nickname, avatar_color)')
        .eq('retado_id', userId)
        .eq('estado', 'pendiente')
        .order('created_at', { ascending: false }),
    ]);
    setPerfil(p);
    setHistorial(h || []);
    setDesafiosPendientes(pend || []);
    setLoading(false);
    // ⚠️ NO BORRAR: guardar en cache para carga instantánea la próxima vez
    try { sessionStorage.setItem(`dtxi_ranked_${userId}`, JSON.stringify({ perfil: p })); } catch(e) {}
  };

  const cargarRankingGlobal = async () => {
    const { data } = await supabase.from('perfiles')
      .select('id, nickname, puntos_ranked, rango_ranked, victorias_ranked, derrotas_ranked, avatar_color')
      .order('puntos_ranked', { ascending: false })
      .limit(50);
    setRankingGlobal(data || []);
  };

  const buscarJugador = async () => {
    if (!busquedaNick.trim()) return;
    setBuscando(true);
    const { data } = await supabase.from('perfiles')
      .select('id, nickname, puntos_ranked, rango_ranked, avatar_color')
      .ilike('nickname', `%${busquedaNick.trim()}%`)
      .neq('id', userId)
      .limit(5);
    setResultadoBusqueda(data || []);
    setBuscando(false);
  };

  const enviarDesafio = async (retadoId, tipo = 'por_los_puntos') => {
    setEnviando(true); setMsg('');

    // Obtener jornada activa
    const { data: ventana } = await supabase.from('ventana_transferencias')
      .select('numero_fecha').eq('activa', true).order('numero_fecha', { ascending: false }).limit(1).maybeSingle();
    const jornada = ventana?.numero_fecha ?? 1;

    // Verificar que no haya un desafío activo entre estos dos
    const { data: existe } = await supabase.from('desafios')
      .select('id').eq('retador_id', userId).eq('retado_id', retadoId)
      .in('estado', ['pendiente', 'activo']).maybeSingle();
    if (existe) { setMsg('Ya tenés un desafío activo con este jugador'); setEnviando(false); return; }

    // Verificar límite de 2 desafíos extra manuales por jornada
    const { count: extrasUsados } = await supabase.from('desafios')
      .select('id', { count: 'exact', head: true })
      .eq('retador_id', userId)
      .eq('jornada', jornada)
      .eq('es_automatico', false);
    if ((extrasUsados || 0) >= 2) {
      setMsg('Ya usaste tus 2 desafíos extra de esta jornada');
      setEnviando(false);
      return;
    }

    const { error } = await supabase.from('desafios').insert({
      retador_id: userId,
      retado_id: retadoId,
      tipo,
      estado: 'pendiente',
      jornada,
      es_automatico: false,
    });
    if (error) { setMsg('Error al enviar el desafío'); }
    else { setMsg('✓ Desafío enviado — esperá que lo acepten'); setBusquedaNick(''); setResultadoBusqueda([]); }
    setEnviando(false);
  };

  const responderDesafio = async (desafioId, aceptar) => {
    const nuevoEstado = aceptar ? 'activo' : 'rechazado';
    // ⚠️ NO BORRAR: remover inmediatamente de ambos estados para evitar doble acción
    setDesafiosPendientes(prev => prev.filter(d => d.id !== desafioId));
    setHistorial(prev => prev.map(d => d.id === desafioId ? { ...d, estado: nuevoEstado } : d));
    await supabase.from('desafios').update({ estado: nuevoEstado }).eq('id', desafioId);
    // ⚠️ NO BORRAR: notificar al navbar para que actualice el badge de pendientes
    window.dispatchEvent(new Event('desafio_respondido'));
    cargarTodo();
  };

  // Barras de progreso del rango
  const RangoBarra = ({ pts }) => {
    const r = getRango(pts);
    const siguiente = RANGOS[RANGOS.indexOf(r) + 1];
    if (!siguiente) return <div style={{ color: r.color, fontSize: '1rem', textAlign: 'center' }}>Rango máximo</div>;
    const progreso = ((pts - r.min) / (siguiente.min - r.min)) * 100;
    return (
      <div style={{ marginTop: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: '#94a3b8', marginBottom: '4px' }}>
          <span>{pts} pts</span><span>{siguiente.min} pts para {siguiente.nombre}</span>
        </div>
        <div style={{ height: '6px', background: '#1e293b', borderRadius: '999px', overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(progreso, 100)}%`, height: '100%', background: r.color, borderRadius: '999px', transition: '0.5s' }} />
        </div>
      </div>
    );
  };

  const SUPABASE_STORAGE_URL = 'https://tbqpambbljrkrdjmlwrs.supabase.co/storage/v1/object/public/division';
  const DIVISIONES = [
    { nombre: 'D',    img: `${SUPABASE_STORAGE_URL}/DDIVISION.png`,    color: '#906234', partidos: 8,  meta: 15,   perm: null },
    { nombre: 'C',    img: `${SUPABASE_STORAGE_URL}/CDIVISION.png`,    color: '#94a3b8', partidos: 8,  meta: 18,   perm: 11  },
    { nombre: 'B',    img: `${SUPABASE_STORAGE_URL}/BDIVISION.png`,    color: '#f59e0b', partidos: 10, meta: 22,   perm: 13  },
    { nombre: 'A',    img: `${SUPABASE_STORAGE_URL}/ADIVISION.png`,    color: '#00f2ff', partidos: 10, meta: 26,   perm: 16  },
    { nombre: 'DTXI', img: `${SUPABASE_STORAGE_URL}/DTXIDIVISION.png`, color: '#a00d00', partidos: 10, meta: 30,   perm: 21, campeon: 30 },
  ];
  const getDivision = (nombre) => DIVISIONES.find(d => d.nombre === nombre) || DIVISIONES[0];

  // ── CICLO BARRA (CON MARCADORES DE UMBRAL) ──────────────────────────────────
  const CicloBarra = ({ division, ptsCiclo, partidosJugados }) => {
    const div = getDivision(division || 'D');

    const objetivo      = div.meta || div.perm || 24;
    const pct           = Math.min((ptsCiclo / objetivo) * 100, 100);
    const pctPerm       = div.perm ? Math.min((div.perm / objetivo) * 100, 100) : null;
    const ascendio      = div.meta && ptsCiclo >= div.meta;
    const permanece     = div.perm && ptsCiclo >= div.perm;
    const ptsFaltanPerm = div.perm ? Math.max(0, div.perm - ptsCiclo) : null;

    // Color de la barra según estado
    const barColor = ascendio ? '#22c55e' : permanece ? div.color : '#ef4444';

    return (
      <div style={{ marginTop: '10px' }}>

        {/* Fila 1: Puntos obtenidos + meta de ascenso */}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', color: '#ffffff', marginBottom: '4px' }}>
          <span style={{ fontWeight: 'bold' }}>{ptsCiclo} PUNTOS OBTENIDOS</span>
          {div.meta && (
            <span style={{ color: div.campeon ? '#f59e0b' : ascendio ? '#22c55e' : '#94a3b8' }}>
              {div.campeon
                ? ascendio ? ' ¡Campeón!' : `${ptsCiclo}/${div.meta} campeonato`
                : ascendio ? ' ¡Ascenso asegurado!' : `${ptsCiclo}/${div.meta} ascenso`}
            </span>
          )}
        </div>

        {/* BARRA CON MARCADORES */}
        <div style={{ position: 'relative', marginBottom: '20px' }}>

          {/* Barra base */}
          <div style={{ height: '8px', background: '#1e293b', borderRadius: '999px', overflow: 'visible', position: 'relative' }}>
            {/* Relleno de progreso */}
            <div style={{
              width: `${pct}%`, height: '100%',
              background: barColor,
              borderRadius: '999px', transition: '0.5s',
              position: 'relative', zIndex: 1
            }} />

            {/* Marcador de PERMANENCIA (línea roja) */}
            {pctPerm !== null && (
              <div style={{
                position: 'absolute',
                left: `${pctPerm}%`,
                top: '-4px',
                width: '2px',
                height: '16px',
                background: '#ef4444',
                zIndex: 3,
                transform: 'translateX(-50%)',
              }} />
            )}

            {/* Marcador de ASCENSO (línea verde) — solo para divisiones sin campeon */}
            {div.meta && !div.campeon && (
              <div style={{
                position: 'absolute',
                left: '100%',
                top: '-4px',
                width: '2px',
                height: '16px',
                background: '#22c55e',
                zIndex: 3,
                transform: 'translateX(-50%)',
              }} />
            )}
          </div>

          {/* Etiquetas debajo de los marcadores */}
          <div style={{ position: 'relative', height: '16px', marginTop: '2px' }}>
            {/* Etiqueta permanencia */}
            {pctPerm !== null && (
              <span style={{
                position: 'absolute',
                left: `clamp(0px, ${pctPerm}%, calc(100% - 30px))`,
                transform: 'translateX(-50%)',
                fontSize: '0.6rem',
                color: '#ef4444',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
              }}>
                {div.perm} PTS
              </span>
            )}
            {/* Etiqueta ascenso — pegada al borde derecho sin salirse */}
            {div.meta && !div.campeon && (
              <span style={{
                position: 'absolute',
                right: '0',
                fontSize: '0.6rem',
                color: '#22c55e',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
              }}>
                ASCENSO
              </span>
            )}
          </div>
        </div>

        {/* Fila 2: Partidos jugados + estado de permanencia */}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', color: '#ffffff', marginBottom: '4px' }}>
          <span>{partidosJugados} / {div.partidos} partidos</span>
          {div.perm && (
            <span style={{ color: permanece ? '#22c55e' : '#ef4444' }}>
              {permanece ? '✓ permanencia asegurada' : `faltán ${ptsFaltanPerm} pts para permanecer`}
            </span>
          )}
        </div>

        {/* Barrita de partidos jugados */}
        <div style={{ display: 'flex', gap: '3px' }}>
          {Array.from({ length: div.partidos }).map((_, i) => (
            <div key={i} style={{
              flex: 1, height: '4px', borderRadius: '2px',
              background: i < partidosJugados ? div.color : '#1e293b',
              transition: '0.3s'
            }} />
          ))}
        </div>
      </div>
    );
  };
  // ────────────────────────────────────────────────────────────────────────────

  const S = {
    card: { background: '#080d14', border: '1px solid #1e293b', borderRadius: '12px', padding: '16px' },
    btn: (c = '#00f2ff') => ({ background: `${c}22`, border: `1px solid ${c}55`, borderRadius: '8px', padding: '7px 16px', color: c, fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer', transition: '0.2s' }),
    input: { background: '#0a0f19', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px 12px', color: '#fff', fontSize: '1rem', width: '100%', boxSizing: 'border-box' },
  };

  if (loading) return <div style={{ padding: '40px', color: '#94a3b8', textAlign: 'center' }}>Cargando...</div>;

  const rango = getRango(perfil?.puntos_ranked || 0);

  return (
    <div style={{ padding: '24px 28px', color: 'white', maxWidth: '750px', margin: '0 auto', background: '#020b18', minHeight: 'calc(100vh - 46px)' }}>

      {/* HEADER */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 4px', fontSize: '1.5rem' }}><span style={{ color: '#00f2ff' }}>Desafío por los puntos</span></h1>
        <p style={{ color: '#e2e8f0', margin: 0, fontSize: '1rem' }}>Reta a otros jugadores y escala el ranking 1v1 hasta llegar a la mejor división</p>
      </div>

      {/* TABS */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '20px' }}>
        {[['perfil','Mi Perfil'], ['buscar','Desafiar'], ['historial','Historial']].map(([t, label]) => (
          <button key={t} onClick={() => { setTab(t); if (t === 'ranking') cargarRankingGlobal(); }}
            style={{ ...S.btn(tab === t ? '#00f2ff' : '#334155'), opacity: tab === t ? 1 : 0.6, fontSize: '1rem' }}>
            {label}
            {t === 'perfil' && desafiosPendientes.length > 0 && (
              <span style={{ background: '#ef4444', color: '#fff', borderRadius: '50%', padding: '0 5px', marginLeft: '6px', fontSize: '0.65rem' }}>{desafiosPendientes.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── TAB PERFIL ────────────────────────────────────────────────────── */}
      {tab === 'perfil' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Card de división y ciclo */}
          {!perfil?.division_ranked ? (
            <div style={{ ...S.card, borderColor: '#1e293b' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: '#0a0f19', border: '2px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', flexShrink: 0 }}>⚪</div>
                <div>
                  <p style={{ margin: '0 0 2px', color: '#94a3b8', fontWeight: 'bold', fontSize: '1.1rem' }}>No competidor</p>
                  <p style={{ margin: 0, color: '#94a3b8', fontSize: '1rem' }}>Jugá desafíos ranked para obtener una división</p>
                </div>
              </div>
            </div>
          ) : (
          <div style={{ ...S.card, borderColor: `${rango.color}44` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
              <div style={{ width: '80px', height: '80px', borderRadius: '16px', background: `${getDivision(perfil?.division_ranked || 'D').color}22`, border: `2px solid ${getDivision(perfil?.division_ranked || 'D').color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                <img src={getDivision(perfil?.division_ranked || 'D').img} alt={perfil?.division_ranked || 'D'}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onError={e => { e.currentTarget.style.display='none'; }} />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, color: getDivision(perfil?.division_ranked || 'D').color, fontWeight: 'bold', fontSize: '1.1rem' }}>División {perfil?.division_ranked || 'D'}</p>
              </div>
              {/* Extras disponibles */}
              {(() => {
                // Jornada activa = la más alta de los desafíos activos/pendientes
                const jornadaActual = historial.length > 0
                  ? Math.max(...historial.map(d => d.jornada || 0))
                  : 0;
                const extrasUsados = historial.filter(d =>
                  !d.es_automatico && d.estado !== 'rechazado' && d.jornada === jornadaActual
                ).length;
                const extrasDisp = Math.max(0, 2 - extrasUsados);
                return (
                  <div style={{ textAlign: 'center', background: '#0a0f19', border: `1px solid ${extrasDisp > 0 ? '#00f2ff33' : '#1e293b'}`, borderRadius: '10px', padding: '10px 14px' }}>
                    <p style={{ color: extrasDisp > 0 ? '#00f2ff' : '#334155', fontWeight: 'bold', fontSize: '1.4rem', margin: 0 }}>{extrasDisp}</p>
                    <p style={{ color: '#94a3b8', fontSize: '0.62rem', margin: '2px 0 0', textTransform: 'uppercase' }}>extras disp.</p>
                  </div>
                );
              })()}
            </div>
            <CicloBarra
              division={perfil?.division_ranked || 'D'}
              ptsCiclo={perfil?.pts_ciclo_actual || 0}
              partidosJugados={perfil?.partidos_ciclo_actual || 0}
            />
          </div>
          )}

          {/* Desafíos automáticos de esta jornada */}
          {(() => {
            const autoActivos = historial.filter(d => d.es_automatico && d.estado === 'activo');
            const autoPendientes = desafiosPendientes.filter(d => d.es_automatico);
            const autoTodos = [...autoPendientes, ...autoActivos].slice(0, 3);
            if (autoTodos.length === 0) return (
              <div style={{ ...S.card, textAlign: 'center', borderColor: '#334155' }}>
                <p style={{ color: '#94a3b8', fontSize: '0.95rem', margin: '0 0 4px' }}>⚡ Desafíos automáticos</p>
                <p style={{ color: '#94a3b8', fontSize: '1rem', margin: 0 }}>Se asignan automáticamente al inicio de cada jornada</p>
              </div>
            );
            return (
              <div style={S.card}>
                <h3 style={{ margin: '0 0 10px', fontSize: '1rem', color: '#00f2ff', textTransform: 'uppercase', letterSpacing: '1px' }}>
                   Desafíos automáticos ({autoTodos.length}/3)
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {autoTodos.map(d => {
                    const rival = d.retador_id === userId ? d.retado : d.retador;
                    const col = rival?.avatar_color || '#00f2ff';
                    return (
                      <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#0a0f19', borderRadius: '8px', border: '1px solid #00f2ff22' }}>
                        <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: col, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', fontWeight: 'bold', color: '#000', flexShrink: 0 }}>
                          {(rival?.nickname || '?').slice(0, 2).toUpperCase()}
                        </div>
                        <span style={{ flex: 1, color: '#e2e8f0', fontSize: '1rem' }}>{rival?.nickname || '?'}</span>
                        <span style={{ color: d.estado === 'activo' ? '#22c55e' : '#f59e0b', fontSize: '1rem', fontWeight: 'bold' }}>
                          {d.estado === 'activo' ? ' Activo' : ' Pendiente'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
            {[
              ['Victoria', perfil?.victorias_ranked || 0, '#22c55e'],
              ['Derrota',  perfil?.derrotas_ranked  || 0, '#ef4444'],
              ['Empate',   perfil?.empates_ranked   || 0, '#f59e0b'],
            ].map(([label, val, color]) => (
              <div key={label} style={{ ...S.card, textAlign: 'center' }}>
                <p style={{ margin: '0 0 4px', color, fontWeight: 'bold', fontSize: '1.4rem' }}>{val > 0 && label === 'Racha' ? `+${val}` : val < 0 && label === 'Racha' ? val : val}</p>
                <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</p>
              </div>
            ))}
          </div>

          {/* Desafíos pendientes */}
          {desafiosPendientes.length > 0 && (
            <div style={S.card}>
              <h3 style={{ margin: '0 0 12px', fontSize: '1rem', color: '#ef4444', textTransform: 'uppercase', letterSpacing: '1px' }}>
                🔔 Desafíos pendientes ({desafiosPendientes.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {desafiosPendientes.map(d => {
                  const rRetador = getRango(0);
                  return (
                    <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: '#0a0f19', borderRadius: '8px', border: '1px solid #ef444433' }}>
                      <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: d.retador?.avatar_color || '#00f2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', fontWeight: 'bold', color: '#000', flexShrink: 0 }}>
                        {(d.retador?.nickname || '?').slice(0, 2).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, color: '#e2e8f0', fontSize: '1rem', fontWeight: '500' }}>{d.retador?.nickname} te desafió</p>
                        <p style={{ margin: '2px 0 0', color: '#94a3b8', fontSize: '1rem' }}>Jornada {d.jornada} · Ranked</p>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        <button onClick={() => responderDesafio(d.id, true)} style={{ ...S.btn('#22c55e'), padding: '5px 12px', fontSize: '1rem' }}>Aceptar</button>
                        <button onClick={() => responderDesafio(d.id, false)} style={{ ...S.btn('#ef4444'), padding: '5px 12px', fontSize: '1rem' }}>Rechazar</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB DESAFIAR ──────────────────────────────────────────────────── */}
      {tab === 'buscar' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={S.card}>
            <p style={{ margin: '0 0 10px', color: '#94a3b8', fontSize: '0.95rem' }}>Buscá un jugador por nickname y retalo</p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input style={{ ...S.input, flex: 1 }} placeholder="Nickname..." value={busquedaNick}
                onChange={e => setBusquedaNick(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && buscarJugador()} />
              <button onClick={buscarJugador} disabled={buscando} style={{ ...S.btn('#00f2ff'), flexShrink: 0 }}>
                {buscando ? '...' : 'Buscar'}
              </button>
            </div>
            {msg && <p style={{ margin: '10px 0 0', color: msg.startsWith('✓') ? '#22c55e' : '#ef4444', fontSize: '0.95rem' }}>{msg}</p>}
          </div>

          {resultadoBusqueda.length > 0 && (
            <div style={S.card}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {resultadoBusqueda.map(u => {
                  const rU = getRango(u.puntos_ranked || 0);
                  return (
                    <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: '#0a0f19', borderRadius: '8px' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: u.avatar_color || '#00f2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.95rem', fontWeight: 'bold', color: '#000', flexShrink: 0 }}>
                        {(u.nickname || '?').slice(0, 2).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, color: '#e2e8f0', fontWeight: '500', fontSize: '1rem' }}>{u.nickname}</p>
                        <p style={{ margin: '2px 0 0', color: rU.color, fontSize: '1rem' }}>{rU.icono} {rU.nombre} · {u.puntos_ranked || 0} pts</p>
                      </div>
                      <button onClick={() => enviarDesafio(u.id)} disabled={enviando}
                        style={{ ...S.btn('#00f2ff'), padding: '5px 14px', fontSize: '1rem', flexShrink: 0 }}>
                        Desafiar
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Desafíos en curso — abajo del buscador */}
          {historial.filter(d => d.estado === 'activo').length > 0 && (
            <div style={{ ...S.card, borderColor: '#00f2ff33' }}>
              <p style={{ margin: '0 0 10px', color: '#00f2ff', fontSize: '1rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>En curso</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {historial.filter(d => d.estado === 'activo').map(d => {
                  const soyRetador = d.retador_id === userId;
                  const rival = soyRetador ? d.retado : d.retador;
                  return (
                    <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', background: '#0a0f19', borderRadius: '8px', border: '1px solid #00f2ff22' }}>
                      <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: rival?.avatar_color || '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', fontWeight: 'bold', color: '#000', flexShrink: 0 }}>
                        {(rival?.nickname || '?').slice(0, 2).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, color: '#e2e8f0', fontSize: '1rem', fontWeight: '500' }}>
                          {soyRetador ? 'Vos' : rival?.nickname} vs {soyRetador ? rival?.nickname : 'Vos'}
                        </p>
                        <p style={{ margin: '2px 0 0', color: '#94a3b8', fontSize: '1rem' }}>Jornada {d.jornada} · Esperando cierre de fecha</p>
                      </div>
                      <span style={{ background: '#00f2ff22', border: '1px solid #00f2ff44', borderRadius: '6px', padding: '3px 10px', color: '#00f2ff', fontSize: '1rem', fontWeight: 'bold' }}>En curso</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Desafíos recibidos pendientes */}
          {desafiosPendientes.length > 0 && (
            <div style={{ ...S.card, borderColor: '#f59e0b44' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '0.95rem', color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Desafíos recibidos ({desafiosPendientes.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {desafiosPendientes.map(d => (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: '#0a0f19', borderRadius: '8px', border: '1px solid #f59e0b22' }}>
                    <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: d.retador?.avatar_color || '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', fontWeight: 'bold', color: '#000', flexShrink: 0 }}>
                      {(d.retador?.nickname || '?').slice(0, 2).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, color: '#e2e8f0', fontSize: '1rem', fontWeight: '500' }}>{d.retador?.nickname || '?'}</p>
                      <p style={{ margin: '2px 0 0', color: '#94a3b8', fontSize: '1rem' }}>Jornada {d.jornada} · Ranked</p>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <button onClick={() => responderDesafio(d.id, true)}
                        style={{ ...S.btn('#22c55e'), padding: '5px 12px', fontSize: '1rem' }}>✓ Aceptar</button>
                      <button onClick={() => responderDesafio(d.id, false)}
                        style={{ ...S.btn('#ef4444'), padding: '5px 12px', fontSize: '1rem' }}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ ...S.card, opacity: 0.6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '1.2rem' }}>🎲</span>
              <div>
                <p style={{ margin: 0, color: '#94a3b8', fontWeight: 'bold', fontSize: '1rem' }}>Matchmaking automático</p>
                <p style={{ margin: '2px 0 0', color: '#94a3b8', fontSize: '1rem' }}>Te empareja con alguien de rango similar automáticamente</p>
              </div>
              <span style={{ marginLeft: 'auto', background: '#1e293b', borderRadius: '6px', padding: '3px 10px', color: '#94a3b8', fontSize: '0.68rem', fontWeight: 'bold', flexShrink: 0 }}>PRÓXIMAMENTE</span>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB HISTORIAL ─────────────────────────────────────────────────── */}
      {tab === 'historial' && (
        <div style={S.card}>
          {historial.length === 0
            ? <p style={{ color: '#94a3b8', textAlign: 'center', padding: '20px 0', fontSize: '1rem' }}>Aún no jugaste ningún desafío ranked</p>
            : (() => {
                // Agrupar por jornada
                const porJornada = {};
                historial.forEach(d => {
                  const j = d.jornada || 0;
                  if (!porJornada[j]) porJornada[j] = [];
                  porJornada[j].push(d);
                });
                const jornadas = Object.keys(porJornada).sort((a, b) => b - a);
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {jornadas.map(jornada => (
                      <div key={jornada}>
                        <p style={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 8px' }}>
                          — Jornada {jornada}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {porJornada[jornada].map(d => {
                  const soyRetador = d.retador_id === userId;
                  const rival = soyRetador ? d.retado : d.retador;
                  const misP  = soyRetador ? d.puntos_retador : d.puntos_retado;
                  const rivP  = soyRetador ? d.puntos_retado  : d.puntos_retador;
                  const gane  = d.ganador_id === userId;
                  const empate = d.estado === 'finalizado' && !d.ganador_id;
                  const color = d.estado === 'pendiente' ? '#f59e0b' : d.estado === 'activo' ? '#00f2ff' : d.estado === 'rechazado' ? '#475569' : gane ? '#22c55e' : empate ? '#f59e0b' : '#ef4444';
                  const label = d.estado === 'pendiente' ? 'Pendiente' : d.estado === 'activo' ? 'En curso' : d.estado === 'rechazado' ? 'Rechazado' : gane ? 'Victoria' : empate ? 'Empate' : 'Derrota';
                  return (
                    <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: '#0a0f19', borderRadius: '8px', border: `1px solid ${color}22` }}>
                      <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: rival?.avatar_color || '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', fontWeight: 'bold', color: '#000', flexShrink: 0 }}>
                        {(rival?.nickname || '?').slice(0, 2).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, color: '#e2e8f0', fontSize: '1rem' }}>
                          vs <span
                            onClick={() => { const rivId = soyRetador ? d.retado_id : d.retador_id; window.location.href = `/palmares/${rivId}`; }}
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
                            {rival?.nickname || '?'}
                          </span>
                        </p>
                        <p style={{ margin: '2px 0 0', color: '#94a3b8', fontSize: '1rem' }}>Jornada {d.jornada} · Ranked</p>
                      </div>
                      {d.estado === 'finalizado' && (
                        <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.95rem', flexShrink: 0 }}>{misP ?? '—'} – {rivP ?? '—'}</p>
                      )}
                      {d.estado === 'finalizado' && (
                        <span style={{ color: gane ? '#22c55e' : empate ? '#f59e0b' : '#ef4444', fontSize: '1rem', fontWeight: 'bold', flexShrink: 0, minWidth: '42px', textAlign: 'right' }}>
                          {gane ? '+25' : empate ? '+10' : '-15'}
                        </span>
                      )}
                      {d.estado === 'finalizado' && rival && (
                        <button onClick={async () => {
                          const rivId = soyRetador ? d.retado_id : d.retador_id;
                          // Buscar el draft del rival en esa jornada
                          const { data: draft } = await supabase
                            .from('drafts').select('equipo, formacion, restricciones, capitan_index')
                            .eq('user_id', rivId).eq('jornada', d.jornada).maybeSingle();
                          setEquipoRival({
                            nombre: rival?.nickname || '?',
                            equipo: draft?.equipo || {},
                            formacion: draft?.formacion || '',
                            restricciones: draft?.restricciones || [],
                            puntos: rivP,
                            capitan_index: draft?.capitan_index ?? null,
                          });
                        }} style={{ background: 'none', border: '1px solid #1e293b', borderRadius: '6px', padding: '3px 10px', color: '#94a3b8', fontSize: '0.68rem', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
                          👁 Equipo
                        </button>
                      )}
                      <span style={{ background: `${color}22`, border: `1px solid ${color}44`, borderRadius: '6px', padding: '3px 10px', color, fontSize: '1rem', fontWeight: 'bold', flexShrink: 0 }}>{label}</span>
                    </div>
                    );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()
          }
        </div>
      )}

      {/* ── TAB RANKING ───────────────────────────────────────────────────── */}
      {tab === 'ranking' && (
        <div style={S.card}>
          {rankingGlobal.length === 0
            ? <p style={{ color: '#94a3b8', textAlign: 'center', padding: '20px 0', fontSize: '1rem' }}>Cargando ranking...</p>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr 80px 60px', padding: '4px 10px', marginBottom: '4px' }}>
                  {['#', 'Jugador', 'División', 'Pts'].map(c => (
                    <span key={c} style={{ color: '#94a3b8', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: c === 'Pts' ? 'right' : 'left' }}>{c}</span>
                  ))}
                </div>
                {rankingGlobal.slice(0, visibleR).map((u, i) => {
                  const divU  = getDivision(u.division_ranked || 'D');
                  const esMio = u.id === userId;
                  const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : null;
                  return (
                    <div key={u.id} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 80px 60px', alignItems: 'center', padding: '10px', background: esMio ? '#00f2ff10' : '#0a0f19', border: `1px solid ${esMio ? '#00f2ff33' : '#1e293b'}`, borderRadius: '8px' }}>
                      <div>{medal ? <span style={{ fontSize: '1.1rem' }}>{medal}</span> : <span style={{ color: '#94a3b8', fontSize: '0.95rem', fontWeight: 'bold' }}>#{i+1}</span>}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: u.avatar_color || '#00f2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 'bold', color: '#000', flexShrink: 0 }}>
                          {(u.nickname || '?').slice(0, 2).toUpperCase()}
                        </div>
                        <span onClick={() => window.location.href = `/palmares/${u.id}`}
                          style={{ color: esMio ? '#00f2ff' : '#e2e8f0', fontSize: '1rem', fontWeight: esMio ? 'bold' : '500', cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
                          {u.nickname}
                          {esMio && <span style={{ color: '#94a3b8', fontSize: '0.85rem', marginLeft: '5px' }}>· vos</span>}
                        </span>
                      </div>
                      <span style={{ color: divU.color, fontSize: '1rem', fontWeight: 'bold' }}>Div. {u.division_ranked || 'D'}</span>
                      <span style={{ textAlign: 'right', color: esMio ? '#00f2ff' : '#e2e8f0', fontWeight: 'bold', fontSize: '1rem' }}>{u.puntos_ranked || 0}</span>
                    </div>
                  );
                })}
                {rankingGlobal.length > visibleR && (
                  <button onClick={() => setVisibleR(v => Math.min(v + 10, 50))}
                    style={{ marginTop: '8px', padding: '8px', background: 'none', border: '1px solid #1e293b', borderRadius: '6px', color: '#94a3b8', fontSize: '0.95rem', cursor: 'pointer' }}>
                    ▼ Ver más ({Math.min(rankingGlobal.length, 50) - visibleR} restantes)
                  </button>
                )}
              </div>
          }
        </div>
      )}

    {/* Modal ver equipo rival */}
    {equipoRival && (
      <ModalVerEquipo
        datos={equipoRival}
        onClose={() => setEquipoRival(null)}
        tipoColor="#a855f7"
      />
    )}
    <Tutorial tutorialId="por_los_puntos" userId={userId} pasos={TUTORIALES.por_los_puntos} botonVerDeNuevo={true} />
    </div>
  );
};

export default RankedPage;